import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch, useSelector } from "react-redux";
import { handlePostRequest } from "../../services/PostTemplate";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import { fetchCashRegisters } from "../../services/cashService";
import styles from "./inStorePickup.module.scss";

const STATUS_CLASS = {
    Scanning: "statusScanning",
    AwaitingPayment: "statusAwaitingPayment",
    AwaitingReceiver: "statusAwaitingReceiver",
    AwaitingApproval: "statusAwaitingApproval",
    Completed: "statusCompleted",
};

const STATUS_LABEL = {
    AwaitingReceiver: "Paid",
};

const InStorePickups = () => {
    const [pickups, setPickups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showRegisterDialog, setShowRegisterDialog] = useState(false);
    const [registers, setRegisters] = useState([]);
    const [pickupRegisterId, setPickupRegisterId] = useState(null);
    const history = useHistory();
    const dispatch = useDispatch();
    const { shift, onShift } = useSelector((s) => s.activeShiftSlice);
    const role = localStorage.getItem("role");
    const isAdmin = role === "admin";
    // Admin has no shift concept at all and is exempt from the pickup-create
    // gate server-side (routes/inStorePickup.js) - only non-admin staff need
    // an active shift before starting one.
    const viewOnly = !isAdmin && !onShift;

    const loadPickups = async () => {
        setLoading(true);
        const res = await handleGetRequest("/in-store-pickup");
        setPickups(res?.data || []);
        setLoading(false);
    };

    useEffect(() => {
        loadPickups();
    }, []);

    const submitCreatePickup = async (cashRegisterId) => {
        setCreating(true);
        try {
            const res = await dispatch(handlePostRequest({ ...(cashRegisterId ? { cashRegisterId } : {}) }, "/in-store-pickup", true, true));
            if (res?.success) {
                setShowRegisterDialog(false);
                history.push(`/in-store-pickup/${res.data._id}`);
            } else {
                toast.error(res?.message || "Failed to create pickup");
            }
        } finally {
            setCreating(false);
        }
    };

    // A shift that skipped register selection entirely (admin/handler, or a
    // clerk who clocked in "remote") needs to pick one before it can be used
    // to collect cash payments on a new in-store pickup.
    const createPickup = async () => {
        if (shift && !shift.cashRegister) {
            setPickupRegisterId(null);
            const res = await fetchCashRegisters();
            setRegisters(res?.data || []);
            setShowRegisterDialog(true);
            return;
        }
        submitCreatePickup();
    };

    const registerOptions = useMemo(
        () => registers.map((r) => ({ label: r.inUseBy ? `${r.name} - in use by ${r.inUseBy}` : r.name, value: r._id, disabled: !!r.inUseBy })),
        [registers],
    );

    const handleConfirmRegister = () => {
        if (!pickupRegisterId) {
            toast.warn("Select a register to continue.");
            return;
        }
        submitCreatePickup(pickupRegisterId);
    };

    const handleDelete = (e, pickup) => {
        e.stopPropagation();
        confirmDialog({
            message: `Delete pickup ${pickup.pickupId}? This can't be undone. Any packages/payments it already recorded stay as-is - only the pickup record itself is removed.`,
            header: "Delete Pickup",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    const response = await axios.delete(`${DEV}/in-store-pickup/${pickup._id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (response?.data?.success) {
                        toast.success("Pickup deleted");
                        setPickups((prev) => prev.filter((p) => p._id !== pickup._id));
                    } else {
                        toast.error(response?.data?.message || "Failed to delete pickup");
                    }
                } catch (error) {
                    console.error("Error deleting pickup:", error);
                    toast.error(error?.response?.data?.message || "Failed to delete pickup");
                }
            },
        });
    };

    const stats = useMemo(() => {
        return {
            total: pickups.length,
            scanning: pickups.filter((p) => p.status === "Scanning").length,
            awaiting: pickups.filter((p) => p.status === "AwaitingPayment" || p.status === "AwaitingReceiver" || p.status === "AwaitingApproval").length,
            completed: pickups.filter((p) => p.status === "Completed").length,
        };
    }, [pickups]);

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className="pi pi-shopping-bag" />
                        </div>
                        <div>
                            <h2>In-Store Pickups</h2>
                            <p className={styles.subtitle}>Scan, verify payment, and release walk-in pickups</p>
                        </div>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <Button
                        label={creating ? "Creating..." : "New Pickup"}
                        icon={creating ? "pi pi-spin pi-spinner" : "pi pi-plus"}
                        className={styles.addButton}
                        disabled={creating || viewOnly}
                        tooltip={viewOnly ? "Clock in to start a pickup" : undefined}
                        onClick={createPickup}
                    />
                </div>
            </div>

            {!loading && pickups.length > 0 && (
                <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconTotal}`}>
                            <i className="pi pi-box" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.total}</div>
                            <div className={styles.statLabel}>Total</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconScanning}`}>
                            <i className="pi pi-qrcode" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.scanning}</div>
                            <div className={styles.statLabel}>Scanning</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconAwaiting}`}>
                            <i className="pi pi-wallet" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.awaiting}</div>
                            <div className={styles.statLabel}>Awaiting Payment/Approval</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconCompleted}`}>
                            <i className="pi pi-check-circle" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.completed}</div>
                            <div className={styles.statLabel}>Completed</div>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading pickups...</span>
                </div>
            ) : pickups.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <i className="pi pi-shopping-bag" />
                    </div>
                    <h4>No in-store pickups yet</h4>
                    <p>Start a new pickup to scan a walk-in client's packages, verify payment, and release them.</p>
                    <Button
                        label="New Pickup"
                        icon="pi pi-plus"
                        className={styles.addButton}
                        disabled={viewOnly}
                        tooltip={viewOnly ? "Clock in to start a pickup" : undefined}
                        onClick={createPickup}
                    />
                </div>
            ) : (
                <div className={styles.grid}>
                    {pickups.map((p) => (
                        <div key={p._id} className={styles.card} onClick={() => history.push(`/in-store-pickup/${p._id}`)}>
                            <div className={styles.cardTop}>
                                <div className={styles.cardIdRow}>
                                    <div className={styles.modeIconSm}>
                                        <i className="pi pi-shopping-bag" />
                                    </div>
                                    <strong>{p.pickupId}</strong>
                                </div>
                                <div className={styles.cardTopActions}>
                                    <span className={`${styles.badge} ${styles[STATUS_CLASS[p.status]]}`}>{STATUS_LABEL[p.status] || p.status}</span>
                                    {isAdmin && <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm" onClick={(e) => handleDelete(e, p)} tooltip="Delete pickup" />}
                                </div>
                            </div>
                            <div className={styles.cardMeta}>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-user" />
                                    <span>
                                        {p.clientId ? `${p.clientId.firstName} ${p.clientId.lastName}` : "No client scanned yet"}
                                    </span>
                                </div>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-box" />
                                    <span>{p.scannedPackageIds?.length || 0} package(s)</span>
                                </div>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-id-card" />
                                    <span>
                                        {p.createdBy?.firstName} {p.createdBy?.lastName}
                                    </span>
                                </div>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-clock" />
                                    <span>{new Date(p.createdAt).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog header="Select a Register" visible={showRegisterDialog} style={{ width: "min(420px, 92vw)" }} onHide={() => setShowRegisterDialog(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "6px" }}>
                    <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>Your shift isn't tied to a cash register yet. Pick one before starting this pickup - it'll stay assigned to your shift afterward.</p>
                    <Dropdown value={pickupRegisterId} options={registerOptions} onChange={(e) => setPickupRegisterId(e.value)} placeholder="Select a register" style={{ width: "100%" }} />
                    <Button label={creating ? "Starting..." : "Continue"} icon={creating ? "pi pi-spin pi-spinner" : "pi pi-check"} className={styles.addButton} onClick={handleConfirmRegister} disabled={creating} />
                </div>
            </Dialog>
        </div>
    );
};

export default InStorePickups;
